import type { ReactElement, ReactNode } from 'react';
import { Checkbox } from '../Field/Field';
import styles from './ToggleMatrix.module.scss';

export interface ToggleMatrixProps<R extends string, C extends string | number> {
  /** Подпись таблицы для экранных читалок. */
  label: string;
  /** Заголовок колонки с подписями строк. */
  corner: ReactNode;
  rows: readonly R[];
  columns: readonly { key: C; label: ReactNode }[];
  isOn(row: R, column: C): boolean;
  /** Подпись флажка для экранных читалок. */
  cellLabel(row: R, column: C): string;
  onToggle(row: R, column: C): void;
  /** Действие строки (удалить) — в последней колонке. */
  rowAction?(row: R): ReactNode;
  disabled?: boolean;
}

/**
 * Матрица флажков «строки × колонки» (сетка слотов: время × дни недели). Шире экрана телефона по
 * природе, поэтому прокручивается внутри себя, а не страница (SYS-UC-11).
 */
export function ToggleMatrix<R extends string, C extends string | number>({ label, corner, rows, columns, isOn, cellLabel, onToggle, rowAction, disabled }: ToggleMatrixProps<R, C>): ReactElement {
  return (
    <div className={styles.wrap}>
      <table className={styles.grid} aria-label={label}>
        <thead>
          <tr>
            <th scope="col">{corner}</th>
            {columns.map((column) => (
              <th key={column.key} scope="col">
                {column.label}
              </th>
            ))}
            {rowAction ? <th /> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row}>
              <th scope="row">{row}</th>
              {columns.map((column) => (
                <td key={column.key}>
                  <Checkbox label={<span className="u-sr-only">{cellLabel(row, column.key)}</span>} checked={isOn(row, column.key)} disabled={disabled} onChange={() => onToggle(row, column.key)} />
                </td>
              ))}
              {rowAction ? <td>{rowAction(row)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
